import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Btn } from '../platform/ui';
import ModelViewer from '../viewer/ModelViewer';
import {
  meshFileByLabel, loadQueue, structureColor, indexClasses, structureName, VIEWER_GROUPS,
} from '../../pages/tools/instrumentsView';

// Прозрачность и видимость по умолчанию: кости полупрозрачные, чтобы были видны зубы и каналы внутри
const GROUP_DEFAULTS = {
  bone: { opacity: 0.35 }, sinus: { opacity: 0.4 }, airway: { opacity: 0.4, hidden: true }, pulp: { hidden: true },
};

/**
 * Результат сегментации в 3D. Модели по одной грузятся с сервера обработки через API сайта (с авторизацией):
 * облегчённые VTK, при их отсутствии STL. Загрузка начинается по кнопке, чтобы не качать десятки файлов зря.
 */
const SegmentationViewer = ({ api, taskId, fileNames, structures, language }) => {
  const { t } = useTranslation();
  const [started, setStarted] = useState(false);
  const [classes, setClasses] = useState(null);                 // null — ещё не загружены

  const meshes = useMemo(() => meshFileByLabel(fileNames), [fileNames]);
  const queue = useMemo(() => loadQueue(structures, meshes), [structures, meshes]);

  // Другой результат — начинаем с кнопки
  useEffect(() => { setStarted(false); setClasses(null); }, [taskId]);

  const start = useCallback(async () => {
    setStarted(true);
    let parsed = {};
    if (fileNames.includes('classes.json')) {
      try {
        const blob = await api.download(taskId, 'classes.json');
        parsed = indexClasses(JSON.parse(await blob.text()));
      } catch (e) { /* цвета по группам */ }
    }
    setClasses(parsed);
  }, [api, taskId, fileNames]);

  const groups = useMemo(() => Object.fromEntries(VIEWER_GROUPS.map((g) => [g, {
    label: t(`pl.instruments.viewer.groups.${g}`), ...(GROUP_DEFAULTS[g] || {}),
  }])), [t]);

  const items = useMemo(() => (classes === null ? [] : queue.map((s) => ({
    id: s.label, name: structureName(s, language), group: s.group, fdi: s.fdi || null,
    color: structureColor(s, classes),
    load: async () => ({ buffer: await (await api.download(taskId, s.file.name)).arrayBuffer(), format: s.file.format }),
  }))), [classes, queue, language, api, taskId]);

  if (!queue.length) return <p className="text-base text-gray-600 mb-4">{t('pl.instruments.viewer.noModels')}</p>;

  return (
    <div className="mb-6">
      <div className="text-lg font-bold mb-2">{t('pl.instruments.viewer.title')}</div>
      {!started ? (
        <>
          <p className="text-sm text-gray-600 mb-3">{t('pl.instruments.viewer.loadHint', { count: queue.length })}</p>
          <Btn onClick={start}>{t('pl.instruments.viewer.load')}</Btn>
        </>
      ) : (
        <ModelViewer key={taskId} items={items} groups={groups} orientation="ras-zyx" />
      )}
    </div>
  );
};

export default SegmentationViewer;
